var Image                         = require("enyo/Image"),
    config                        = require("app/config"),
    createBunnyImageUrl           = require("app/utils/createBunnyImageUrl"),
    createThumborImageUrl         = require("app/utils/createThumborImageUrl"),
    get                           = require("object-path").get,
    kind                          = require("enyo/kind"),
    relativeImageUrlToAbsoluteUrl = require("app/utils/relativeImageUrlToAbsoluteUrl"),
    resolveImageDimensions,
    utils                         = require("enyo/utils");

/**
 * The default implementation of finding the dimensions of the image. You can
 * provide your own method on the kind if needed. This implementation will only
 * be used if no `resolveImageDimensions` function exists on the kind once kind
 * construction has completed.
 *
 * @returns  {Object}  The dimensions
 * @private
 */
resolveImageDimensions = function() {
    return this.getBounds();
};

/**
 * Use this mixin to use an image optimization service.
 *
 * @mixin app.ui.ImageOptimizerSupport
 * @public
 */
module.exports = {

    /**
     * @private
     */
    name: "app.ui.ImageOptimizerSupport",

    /**
     * This setting allows some control on where the url of the optimized image
     * will be applied.
     *
     * Supported options are:
     *  - `srcProp`          Applies the resolved image url to the 'src'
     *                       property.
     *  - `srcAttribute`     Applies the resolved image to the src attribute.
     *  - `backgroundImage`  Applies the resolved image as css background image.
     *
     * @name      optimizedImageUrlProp
     * @type      {?string}
     * @memberof  app.ui.ImageOptimizerSupport
     * @public
     */

    /**
     * The source of the un-optimized image
     *
     * @name      rawImageSrc
     * @type      {?string}
     * @memberof  app.ui.ImageOptimizerSupport
     * @public
     */

    /**
     * The increment in pixel count used when calculating the optimized
     * resolution.
     *
     * @name      imageResolutionStepSize
     * @type      {number}
     * @memberof  app.ui.ImageOptimizerSupport
     * @public
     */

    /**
     * Explicitly disallow png files.
     *
     * @name      imageOptimizerNoPng
     * @type      {?boolean}
     * @memberof  app.ui.ImageOptimizerSupport
     * @public
     */

    /**
     * @memberof  app.ui.ImageOptimizerSupport
     * @method
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            sup.apply(this, arguments);

            // Apply default implementation if no custom implementation has been
            // supplied
            if (!utils.isFunction(this.resolveImageDimensions)) {
                this.resolveImageDimensions = resolveImageDimensions;
            }

            // Attempt to auto-detect where we should apply the optimized
            // image's url.
            if (this.optimizedImageUrlProp === undefined) {
                if (this instanceof Image) {
                    this.optimizedImageUrlProp = "srcProp";
                }
                else if (this.get("tag") === "img") {
                    this.optimizedImageUrlProp = "srcAttribute";
                }
                else {
                    this.optimizedImageUrlProp = "backgroundImage";
                }
            }

            // Default to step size of 100 pixels
            if (this.imageResolutionStepSize === undefined) {
                this.imageResolutionStepSize = 100;
            }

            // Flatten png files
            if (this.imageOptimizerNoPng === undefined) {
                this.imageOptimizerNoPng = true;
            }
        };
    }),

    /**
     * @memberof  app.ui.ImageOptimizerSupport
     * @method
     * @private
     */
    rawImageSrcChanged: function() {
        var optimizedImgUrl;

        optimizedImgUrl = this.resolveOptimizedImageUrl();
        this.applyOptimizedImage.apply(this, optimizedImgUrl);
    },

    /**
     * @memberof  app.ui.ImageOptimizerSupport
     * @method
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            var optimizedImgUrl;

            sup.apply(this, arguments);

            optimizedImgUrl = this.resolveOptimizedImageUrl();
            this.applyOptimizedImage.apply(this, optimizedImgUrl);
        };
    }),

    /**
     * Resolves an image url as well as width and height in pixels if possible.
     * If any of them cannot be generated, all values will be set to `null`.
     *
     * Inline images pass through unmodified
     *
     * @returns   {Array}  An array containing [url, width, height]
     * @memberof  app.ui.ImageOptimizerSupport
     * @method
     * @private
     */
    resolveOptimizedImageUrl: function() {
        var rawImageSrc      = this.rawImageSrc || null,
            devicePixelRatio = Math.min(1.5, window.devicePixelRatio || 1),
            stepSize         = this.get("imageResolutionStepSize"),
            noPng            = this.get("imageOptimizerNoPng"),
            imageDimensions,
            width,
            height,
            extension,
            src;

        // Ignore inline images
        if (rawImageSrc
            && rawImageSrc.src
            && rawImageSrc.src.substring(0, 10) === "data:image"
        ) {
            return [
                get(this, "rawImageSrc.src", null),
                get(this, "rawImageSrc.width", null),
                get(this, "rawImageSrc.height", null)
            ];
        }

        // Turn relative url's into absolute ones
        if (rawImageSrc) {
            rawImageSrc.src = relativeImageUrlToAbsoluteUrl(rawImageSrc.src);
        }

        // Insufficient data to set an image source
        if (!rawImageSrc
            || Number.isNaN(parseFloat(rawImageSrc.width, 10)) || parseFloat(rawImageSrc.width, 10) <= 0
            || Number.isNaN(parseFloat(rawImageSrc.height, 10)) || parseFloat(rawImageSrc.height, 10) <= 0
            || Number.isNaN(parseFloat(rawImageSrc.ratio, 10)) || parseFloat(rawImageSrc.ratio, 10) <= 0
            || !rawImageSrc.src) {
            return [null, null, null];
        }

        // Determine the display dimensions of the image
        imageDimensions = this.resolveImageDimensions();

        // No dimensions, no image
        if (!imageDimensions.width || !imageDimensions.height) {
            return [null, null, null];
        }

        // Calculate the resolution needed for the image to cover the calculated
        // imageDimensions
        if (rawImageSrc.ratio < imageDimensions.width / imageDimensions.height) {
            width  = Math.min(
                Math.ceil(rawImageSrc.width),
                Math.ceil((imageDimensions.width * devicePixelRatio) / stepSize) * stepSize
            );
            height = Math.ceil(width / rawImageSrc.ratio);
        }
        else {
            height = Math.min(
                Math.ceil(rawImageSrc.height),
                Math.ceil((imageDimensions.height * devicePixelRatio) / stepSize) * stepSize
            );
            width  = Math.ceil(height * rawImageSrc.ratio);
        }

        // Determine file type extension
        extension = rawImageSrc.src.substr((Math.max(0, rawImageSrc.src.lastIndexOf(".")) || Infinity) + 1);

        // If png files are disallowed, explicitly convert png to jpg.
        if (extension.toLowerCase() === "png" && noPng) {
            extension = "jpg";
        }

        switch (get(config, "imageOptimizer.service", null)) {
            case "thumbor":
                src = createThumborImageUrl(
                    rawImageSrc.src,
                    width,
                    height,
                    extension
                );
                break;
            case "bunny":
                src = createBunnyImageUrl(
                    rawImageSrc.src,
                    width,
                    height,
                    extension
                );
                break;
            case null:
            default:
                src = rawImageSrc.src;
                break;
        }

        return [src, width, height];
    },

    /**
     * Applies the image source to the DOM.
     *
     * @param     {?string}  src     The image source
     * @param     {?number}  width   The width of the image
     * @param     {?number}  height  The height of the image
     * @memberof  app.ui.ImageOptimizerSupport
     * @method
     * @private
     */
    applyOptimizedImage: function(src, width, height) {
        switch (this.optimizedImageUrlProp) {
            case "srcProp":
                this.set("src", src);
                this.setAttribute("width", width);
                this.setAttribute("height", height);
                break;
            case "srcAttribute":
                this.setAttribute("src", src);
                this.setAttribute("width", width);
                this.setAttribute("height", height);
                break;
            case "backgroundImage":
            default:
                this.applyStyle("background-image", src ? "url('" + src + "')" : "none");
                break;
        }
    }

};
